import React, { useState, useEffect, useRef } from 'react';
import { useStock } from '../../context/StockContext';
import toast from 'react-hot-toast';

const B = '#0B3D6E', T = '#0F6E56';

export default function StockScanner() {
  const { products, addMovement } = useStock();
  const [barcode, setBarcode] = useState('');
  const [found, setFound] = useState(null);
  const [mv, setMv] = useState({ type:'out', quantity:1, reason:'Vente client' });
  const [saving, setSaving] = useState(false);
  const inputRef = useRef();

  useEffect(() => { inputRef.current?.focus(); }, []);

  const handleSearch = (code) => {
    const q = code.trim().toLowerCase();
    const product = products.find(p => p.barcode?.toLowerCase() === q || p.name.toLowerCase().includes(q));
    setFound(product || null);
    if (!product && q.length > 2) toast.error(`Produit "${q}" introuvable`);
  };

  const handleMovement = async (e) => {
    e.preventDefault();
    if (!found) return;
    setSaving(true);
    try {
      await addMovement({ productId:found._id, ...mv, quantity:Number(mv.quantity) });
      toast.success(`✅ ${mv.type==='out'?'Sortie':'Entrée'} enregistrée — ${found.name}`);
      setBarcode(''); setFound(null);
      setMv({ type:'out', quantity:1, reason:'Vente client' });
      inputRef.current?.focus();
    } catch(err) { toast.error(err.response?.data?.message||'Erreur'); }
    finally { setSaving(false); }
  };

  const stockColor = (p) => p.quantity===0?'#dc2626':p.quantity<=p.minQuantity?'#d97706':T;

  return (
    <div style={{ maxWidth:600, margin:'0 auto', padding:'1.5rem' }}>
      <h1 style={{ fontSize:20, fontWeight:600, marginBottom:6 }}>Scanner / Recherche rapide</h1>
      <p style={{ fontSize:13, color:'#64748b', marginBottom:'1.5rem' }}>Scannez un code-barres ou tapez le nom du produit pour enregistrer rapidement un mouvement.</p>

      {/* Search input */}
      <div style={{ background:'#fff', border:'1px solid #e2e8f0', borderRadius:12, padding:'1.5rem', marginBottom:16, boxShadow:'0 1px 4px rgba(0,0,0,0.06)' }}>
        <label style={{ fontSize:11, fontWeight:600, color:'#64748b', textTransform:'uppercase', letterSpacing:'0.5px', display:'block', marginBottom:8 }}>
          Code-barres ou nom du produit
        </label>
        <div style={{ display:'flex', gap:10 }}>
          <input ref={inputRef} type="text" value={barcode}
            onChange={e=>setBarcode(e.target.value)}
            onKeyDown={e=>{ if(e.key==='Enter') handleSearch(barcode); }}
            placeholder="Scanner ou taper le nom / code-barres..."
            style={{ flex:1, border:'2px solid #e2e8f0', borderRadius:9, padding:'12px 14px', fontSize:14, outline:'none', fontFamily:'monospace' }}
            onFocus={e=>e.target.style.borderColor=B} onBlur={e=>e.target.style.borderColor='#e2e8f0'}/>
          <button onClick={()=>handleSearch(barcode)} style={{ background:B, color:'#fff', border:'none', padding:'12px 18px', borderRadius:9, fontSize:13, fontWeight:600, cursor:'pointer' }}>
            🔍 Chercher
          </button>
        </div>
        <div style={{ marginTop:10, display:'flex', gap:8, flexWrap:'wrap' }}>
          <div style={{ fontSize:11, color:'#94a3b8' }}>Suggestions rapides :</div>
          {products.slice(0,5).map(p=>(
            <button key={p._id} onClick={()=>{ setBarcode(p.name); setFound(p); }} style={{ fontSize:11, background:'#f1f5f9', border:'1px solid #e2e8f0', padding:'3px 9px', borderRadius:6, cursor:'pointer', color:'#64748b' }}>
              {p.name.slice(0,20)}
            </button>
          ))}
        </div>
      </div>

      {/* Product found */}
      {found && (
        <div style={{ background:'#fff', border:`2px solid ${T}`, borderRadius:12, overflow:'hidden', boxShadow:'0 4px 16px rgba(15,110,86,0.15)' }}>
          <div style={{ background:T, padding:'12px 16px', display:'flex', alignItems:'center', gap:10 }}>
            <span style={{ fontSize:20 }}>✅</span>
            <div style={{ color:'#fff' }}>
              <div style={{ fontSize:14, fontWeight:600 }}>{found.name}</div>
              {found.barcode && <div style={{ fontSize:11, opacity:0.8, fontFamily:'monospace' }}>{found.barcode}</div>}
            </div>
          </div>
          <div style={{ padding:'1.5rem' }}>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:10, marginBottom:16 }}>
              {[['Stock actuel',`${found.quantity} ${found.unit}`,stockColor(found)],['Seuil min',`${found.minQuantity} ${found.unit}`,'#64748b'],['Catégorie',found.category,'#64748b']].map(([l,v,c])=>(
                <div key={l} style={{ background:'#f8fafc', borderRadius:8, padding:'10px' }}>
                  <div style={{ fontSize:10, color:'#94a3b8', marginBottom:2 }}>{l}</div>
                  <div style={{ fontSize:14, fontWeight:700, color:c }}>{v}</div>
                </div>
              ))}
            </div>

            <form onSubmit={handleMovement} style={{ display:'flex', flexDirection:'column', gap:12 }}>
              <div>
                <label style={{ fontSize:11, fontWeight:600, color:'#64748b', display:'block', marginBottom:6 }}>Type</label>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:8 }}>
                  {[['out','📤 Sortie','#dc2626'],['in','📥 Entrée',T],['adjust','🔄 Ajuster','#7C3AED']].map(([v,l,c])=>(
                    <button type="button" key={v} onClick={()=>setMv({...mv,type:v,reason:v==='out'?'Vente client':v==='in'?'Achat fournisseur':'Inventaire physique'})} style={{ background:mv.type===v?c:'#f8fafc', color:mv.type===v?'#fff':'#64748b', border:`1px solid ${mv.type===v?c:'#e2e8f0'}`, padding:'8px 4px', borderRadius:8, fontSize:11, fontWeight:600, cursor:'pointer' }}>{l}</button>
                  ))}
                </div>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                <div>
                  <label style={{ fontSize:11, color:'#64748b', display:'block', marginBottom:4 }}>Quantité</label>
                  <input type="number" min="1" value={mv.quantity} onChange={e=>setMv({...mv,quantity:e.target.value})} style={{ width:'100%', border:'1px solid #e2e8f0', borderRadius:8, padding:'9px', fontSize:16, fontWeight:700, textAlign:'center', outline:'none', background:'#f8fafc', boxSizing:'border-box' }}/>
                </div>
                <div>
                  <label style={{ fontSize:11, color:'#64748b', display:'block', marginBottom:4 }}>Motif</label>
                  <input style={{ width:'100%', border:'1px solid #e2e8f0', borderRadius:8, padding:'9px 12px', fontSize:13, outline:'none', background:'#f8fafc', boxSizing:'border-box' }} value={mv.reason} onChange={e=>setMv({...mv,reason:e.target.value})}/>
                </div>
              </div>
              {mv.type !== 'adjust' && (
                <div style={{ background:'#f8fafc', borderRadius:8, padding:'10px 12px', fontSize:13, color:'#475569' }}>
                  Après mouvement : <strong style={{ color:mv.type==='in'?T:'#dc2626', fontSize:15 }}>{mv.type==='in'?found.quantity+Number(mv.quantity):Math.max(0,found.quantity-Number(mv.quantity))} {found.unit}</strong>
                </div>
              )}
              <button type="submit" disabled={saving} style={{ background:saving?'#94a3b8':mv.type==='in'?T:mv.type==='out'?'#dc2626':'#7C3AED', color:'#fff', border:'none', padding:'13px', borderRadius:9, fontSize:14, fontWeight:700, cursor:'pointer', opacity:saving?0.7:1 }}>
                {saving?'⏳ Enregistrement...':'✅ Confirmer le mouvement'}
              </button>
            </form>
          </div>
        </div>
      )}

      {!found && products.length > 0 && (
        <div style={{ background:'#fff', border:'1px solid #e2e8f0', borderRadius:12, padding:'2rem', textAlign:'center', color:'#94a3b8' }}>
          <div style={{ fontSize:40, marginBottom:10 }}>📷</div>
          <div style={{ fontSize:14, fontWeight:500 }}>Prêt à scanner</div>
          <div style={{ fontSize:12, marginTop:4 }}>{products.length} produit(s) dans votre stock</div>
        </div>
      )}
    </div>
  );
}

